<template>
  <header class="tw-mb-11">
    <h2 class="tw-text-[1.7rem] tw-font-bold">
      {{ t('auth.titles.login_to_account').toUpperCase() }}
    </h2>
    <p class="tw-text-base tw-font-light">{{ t('auth.titles.enter_login') }}</p>
  </header>

  <div
    v-if="errorMessage"
    class="tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded tw-mb-4"
    role="alert"
  >
    {{ errorMessage }}
  </div>

  <Form
    class="tw-flex tw-flex-col"
    :validation-schema="loginFormSchema"
    v-slot="{ errors, isSubmitting }"
    @submit="onUserLogin"
  >
    <div class="tw-mb-7">
      <label for="email" class="tw-text-[0.9rem] tw-mb-1 tw-block">
        {{ t('common.titles.email') }}
      </label>

      <Field
        id="email"
        name="email"
        type="email"
        class="tw-w-full tw-border tw-border-[#d8d8d8] tw-rounded-lg tw-text-[0.9rem] tw-px-3 tw-py-1.5 placeholder:tw-text-[#dcdcdc] placeholder:tw-text-[0.9rem] focus:tw-outline-none"
        :class="{ 'tw-border-danger': errors.email }"
        :placeholder="t('auth.titles.enter_email')"
      />

      <div v-if="errors.email" class="tw-text-danger tw-text-xs tw-mt-1">{{ errors.email }}</div>
    </div>

    <div class="tw-mb-1.5">
      <label for="password" class="tw-text-[0.9rem] tw-mb-1 tw-block">
        {{ t('auth.titles.password') }}
      </label>

      <div class="tw-relative">
        <Field
          id="password"
          name="password"
          :type="isPasswordShown ? 'text' : 'password'"
          class="tw-w-full tw-border tw-border-[#d8d8d8] tw-rounded-lg tw-text-[0.9rem] tw-px-3 tw-py-1.5 placeholder:tw-text-[#dcdcdc] placeholder:tw-text-[0.9rem] focus:tw-outline-none"
          :class="{ 'tw-border-danger': errors.password }"
          :placeholder="t('auth.titles.enter_password')"
        />

        <div v-if="errors.password" class="tw-text-danger tw-text-xs tw-mt-1">
          {{ errors.password }}
        </div>

        <i
          v-if="!isPasswordShown"
          class="icon-eye tw-absolute tw-top-2 tw-right-2.5 tw-cursor-pointer before:tw-text-[#38678c]"
          :title="t('auth.titles.show_password')"
          @click="togglePasswordVisibility"
        ></i>
        <i
          v-else
          class="icon-eye-slash tw-absolute tw-top-2 tw-right-2.5 tw-cursor-pointer before:tw-text-[#38678c]"
          :title="t('auth.titles.hide_password')"
          @click="togglePasswordVisibility"
        ></i>
      </div>
    </div>

    <span
      class="tw-self-end tw-text-[#86a4ba] tw-text-[0.8rem] tw-underline tw-cursor-pointer"
      @click="emit('forgotPassword')"
    >
      {{ t('auth.titles.forgot_your_password') }}?
    </span>

    <button
      class="tw-mt-8 tw-bg-cta-primary tw-text-white tw-rounded-lg tw-min-h-[2.4rem] disabled:tw-opacity-70"
      :disabled="isSubmitting"
    >
      {{ t('auth.titles.login') }}
    </button>
  </Form>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useAuthStore } from '@/stores/auth.store';
import { storeToRefs } from 'pinia';

import { Form, Field } from 'vee-validate';
import { object, string } from 'yup';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const loginFormSchema = object().shape({
  email: string().required(t('auth.titles.email_required')).email(t('auth.titles.email_invalid')),
  password: string().required(t('auth.forms.password_required')),
});

const isPasswordShown = ref(false);

const emit = defineEmits<{
  (e: 'forgotPassword'): void;
  (e: 'nextChallenge', nextChallengeType: string): void;
  (e: 'needsTenantSelection'): void;
}>();

const { nextChallenge, errorMessage, needsTenantSelection } = storeToRefs(useAuthStore());
const { enterCredentials } = useAuthStore();

const onUserLogin = async (values: any) => {
  await enterCredentials(values.email, values.password);

  // Check if user needs to select a tenant
  if (needsTenantSelection.value) {
    emit('needsTenantSelection');
  } else if (nextChallenge.value) {
    emit('nextChallenge', nextChallenge.value);
  }
};

const togglePasswordVisibility = () => {
  isPasswordShown.value = !isPasswordShown.value;
};
</script>

<style scoped>
/* Using Tailwind classes - no additional styles needed */
</style>
